import { useState, useEffect, useCallback, useRef } from "react";
import { ThemeProvider, useTheme } from "./ThemeContext";
import { STATUS } from "./theme";
import { get, put } from "./api";
import T from "./i18n";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Pedidos from "./pages/Pedidos";
import Entregadores from "./pages/Entregadores";
import RelPedidos from "./pages/RelPedidos";
import RelFinanceiro from "./pages/RelFinanceiro";
import RelEntregadores from "./pages/RelEntregadores";
import Cadastro from "./pages/Cadastro";
import Configuracao from "./pages/Configuracao";
import BotWhatsApp from "./pages/BotWhatsApp";

const API_URL = "https://mixupbr.com.br/api";
const STORAGE_KEY = "mixup_loja_auth";

// ─── Tela de Login ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const { theme: C } = useTheme();
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const fazer = async () => {
    if (!login.trim() || !senha.trim()) { setErro("Preencha login e senha."); return; }
    setCarregando(true); setErro("");
    try {
      const r = await fetch(`${API_URL}/bot/index.php?action=login_loja`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim(), senha }),
      });
      const d = await r.json();
      if (!r.ok || d.erro) { setErro(d.erro || "Credenciais inválidas."); setCarregando(false); return; }
      onLogin({ id: d.loja_id, nome: d.nome, login: d.login });
    } catch { setErro("Erro de conexão. Tente novamente."); }
    setCarregando(false);
  };

  const inp = { width: "100%", padding: "11px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 380, background: C.card, borderRadius: 20, padding: 36, border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontWeight: 800, fontSize: 28 }}>
            <span style={{ color: C.red }}>Mix</span><span style={{ color: C.green }}>Up</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600, letterSpacing: "0.08em", marginTop: 4, textTransform: "uppercase" }}>Painel do Lojista</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Login</label>
          <input style={inp} value={login} onChange={e => setLogin(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fazer()}
            placeholder="usuario_da_loja" autoCapitalize="none" autoComplete="username" />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Senha</label>
          <input style={inp} type="password" value={senha} onChange={e => setSenha(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fazer()}
            placeholder="••••••••" autoComplete="current-password" />
        </div>

        {erro && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 14, textAlign: "center" }}>{erro}</div>}

        <button onClick={fazer} disabled={carregando}
          style={{ width: "100%", padding: 14, borderRadius: 10, border: "none", background: C.red, color: "#fff", fontWeight: 700, fontSize: 15, cursor: carregando ? "wait" : "pointer", opacity: carregando ? 0.7 : 1 }}>
          {carregando ? "Entrando..." : "Entrar"}
        </button>
      </div>
    </div>
  );
}

// ─── App principal ────────────────────────────────────────────────────────────
function AppInner() {
  const { theme: C } = useTheme();

  const [loja, setLoja] = useState(() => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  const handleLogin = (dadosLoja) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dadosLoja));
    setLoja(dadosLoja);
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setLoja(null);
  };

  if (!loja) return <LoginScreen onLogin={handleLogin} />;

  const LOJA_ID = loja.id;

  return <AppLogado C={C} LOJA_ID={LOJA_ID} loja={loja} onLogout={handleLogout} />;
}

// ─── App logado ───────────────────────────────────────────────────────────────
function AppLogado({ C, LOJA_ID, loja, onLogout }) {
  const [page, setPage] = useState("dashboard");
  const [relGrupo, setRelGrupo] = useState("pedidos");
  const [relSub, setRelSub] = useState("vendidos");
  const [cadSub, setCadSub] = useState("categorias");
  const [cfgSub, setCfgSub] = useState("configuracao_tela");
  const [lang, setLang] = useState(() => localStorage.getItem("mixup_lang") || "pt");
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const [chatTotalNaoLidos, setChatTotalNaoLidos] = useState(0);
  const chatPrevTotalRef = useRef(0);
  const chatSomRef = useRef(null);

  const playChatSound = () => {
    try {
      if (!chatSomRef.current) chatSomRef.current = new Audio("/mixup-lojista/notification.mp3");
      chatSomRef.current.currentTime = 0;
      chatSomRef.current.play().catch(() => {});
    } catch {}
  };

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?tipo=loja&id=${LOJA_ID}`);
        const d = await r.json();
        if (d.ok) {
          const total = (d.conversas || []).reduce((sum, c) => sum + (parseInt(c.nao_lidos) || 0), 0);
          setChatTotalNaoLidos(total);
          if (total > chatPrevTotalRef.current && chatPrevTotalRef.current >= 0) playChatSound();
          chatPrevTotalRef.current = total;
        }
      } catch {}
    };
    poll();
    const i = setInterval(poll, 5000);
    return () => clearInterval(i);
  }, [LOJA_ID]);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  const fetchOrders = useCallback(async () => {
    try {
      const res = await get("pedidos", { loja_id: LOJA_ID, limite: 500 });
      if (res.dados) setOrders(res.dados);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [LOJA_ID]);

  useEffect(() => { fetchOrders(); const i = setInterval(fetchOrders, 10000); return () => clearInterval(i); }, [fetchOrders]);
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { localStorage.setItem("mixup_lang", lang); }, [lang]);

  const advanceOrder = async (id) => {
    const o = orders.find(x => x.id === id);
    if (!o) return;
    const cfg = STATUS[o.status];
    if (!cfg?.ns) return;
    try {
      await put("pedidos", id, { status: cfg.ns });
      notify("Pedido atualizado!");
      fetchOrders();
    } catch { notify("Erro ao atualizar", false); }
  };

  const rejectOrder = async (id) => {
    try {
      await put("pedidos", id, { status: "cancelado", motivo_cancelamento: "Recusado pelo lojista" });
      notify("Pedido cancelado", false);
      fetchOrders();
    } catch { notify("Erro ao cancelar", false); }
  };

  const novos = orders.filter(o => o.status === "novo").length;
  const collapsed = page === "pedidos";
  const _ = (k) => T(lang, k);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "Segoe UI, system-ui, sans-serif", color: C.text, transition: "background .3s, color .3s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}body{background:${C.bg}}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes glow{0%,100%{box-shadow:0 0 6px ${C.red}40}50%{box-shadow:0 0 18px ${C.red}80}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .nav-item{transition:all .2s;cursor:pointer;border-radius:10px}.nav-item:hover{background:rgba(128,128,128,.08)}
        .sub-g{transition:all .15s}.sub-g:hover{background:rgba(128,128,128,.07)}
        .sub-s{transition:all .15s}.sub-s:hover{background:rgba(128,128,128,.06)!important}
        .stat-card{transition:all .3s}.stat-card:hover{transform:translateY(-4px);box-shadow:0 10px 30px rgba(0,0,0,.2)!important}
        .order-card{transition:all .2s;cursor:pointer}.order-card:hover{background:${C.cardHover}!important;transform:translateY(-1px)}
        .act-btn{transition:all .15s;cursor:pointer}.act-btn:hover{transform:translateY(-1px);filter:brightness(1.1)}.act-btn:active{transform:scale(.97)}
        .rej-btn{transition:all .15s;cursor:pointer}.rej-btn:hover{background:${C.red}20!important;color:${C.red}!important}
        .form-btn{transition:all .15s;cursor:pointer}.form-btn:hover{filter:brightness(1.1);transform:translateY(-1px)}
        input:focus,select:focus{border-color:${C.red}!important}
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, background: toast.ok ? C.green : C.red, color: "#fff", padding: "12px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14, boxShadow: "0 8px 30px rgba(0,0,0,.4)", animation: "slideDown .3s ease-out" }}>{toast.msg}</div>
      )}

      <Sidebar
        page={page} setPage={setPage}
        relGrupo={relGrupo} setRelGrupo={setRelGrupo}
        relSub={relSub} setRelSub={setRelSub}
        cadSub={cadSub} setCadSub={setCadSub}
        cfgSub={cfgSub} setCfgSub={setCfgSub}
        collapsed={collapsed} novos={novos} clock={clock} lang={lang}
        nomeLoja={loja.nome}
        onLogout={onLogout}
      />

      <div style={{ flex: 1, padding: page === "pedidos" ? 16 : 24, overflowY: "auto", minHeight: "100vh" }}>
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 40, height: 40, border: "3px solid " + C.red, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 16px" }} />
              <div style={{ color: C.textSoft, fontWeight: 600 }}>Carregando...</div>
            </div>
          </div>
        )}

        {!loading && page === "dashboard" && <Dashboard orders={orders} clock={clock} goToPedidos={() => setPage("pedidos")} lang={lang} />}
        {!loading && page === "pedidos" && <Pedidos orders={orders} onAdvance={advanceOrder} onReject={rejectOrder} onRefresh={fetchOrders} lang={lang} lojaId={LOJA_ID} />}
        {!loading && page === "entregadores" && <Entregadores lojaId={LOJA_ID} lang={lang} />}

        {!loading && page === "relatorios" && (
          <div style={{ animation: "fadeUp .4s ease-out" }}>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 800, fontSize: 28 }}>{_("relatorios")}</h1>
            </div>
            {relGrupo === "pedidos"      && <RelPedidos     orders={orders} sub={relSub} lang={lang} />}
            {relGrupo === "financeiro"   && <RelFinanceiro  orders={orders} sub={relSub} lang={lang} />}
            {relGrupo === "entregadores" && <RelEntregadores orders={orders} sub={relSub} lang={lang} />}
          </div>
        )}

        {!loading && page === "cadastro" && <Cadastro     sub={cadSub} lojaId={LOJA_ID} lang={lang} />}
        {!loading && page === "config"   && <Configuracao sub={cfgSub} lojaId={LOJA_ID} lang={lang} setLang={setLang} />}
        {!loading && page === "bot"     && <BotWhatsApp lojaId={LOJA_ID} nomeLoja={loja.nome} />}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppInner />
    </ThemeProvider>
  );
}
